/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { SignatureCanvasHandle, SignatureCanvasProps } from './SignatureCanvas.types';

// Fixed drawing resolution — scaled down to the container via CSS, matching the
// HTML mock's `<canvas width="900" height="240">` + `.people-signature-pad canvas { height: 124px }`.
const CANVAS_WIDTH = 900;
const CANVAS_HEIGHT = 240;
const INK_COLOR = '#123b6d';

/** Web signature pad — a raw HTML canvas driven by pointer events, matching the HTML mock exactly. */
const SignatureCanvas = forwardRef<SignatureCanvasHandle, SignatureCanvasProps>(
  ({ disabled, onHasContentChange }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const isDrawing = useRef(false);
    const lastPoint = useRef<{ x: number; y: number } | null>(null);
    const hasContent = useRef(false);

    const getPoint = (e: any) => {
      const canvas = e.currentTarget as HTMLCanvasElement;
      const rect = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) * (canvas.width / rect.width),
        y: (e.clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const markHasContent = () => {
      if (!hasContent.current) {
        hasContent.current = true;
        onHasContentChange(true);
      }
    };

    const onPointerDown = (e: any) => {
      if (disabled) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      const point = getPoint(e);
      const ctx = e.currentTarget.getContext('2d');
      if (ctx) {
        ctx.fillStyle = INK_COLOR;
        ctx.beginPath();
        ctx.arc(point.x, point.y, 2.4, 0, Math.PI * 2);
        ctx.fill();
      }
      isDrawing.current = true;
      lastPoint.current = point;
      markHasContent();
    };

    const onPointerMove = (e: any) => {
      if (!isDrawing.current || !lastPoint.current) return;
      e.preventDefault();
      const point = getPoint(e);
      const ctx = e.currentTarget.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = INK_COLOR;
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
      }
      lastPoint.current = point;
    };

    const onPointerUp = (e: any) => {
      isDrawing.current = false;
      lastPoint.current = null;
      if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    };

    useImperativeHandle(ref, () => ({
      clear: () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (ctx && canvas) ctx.clearRect(0, 0, canvas.width, canvas.height);
        hasContent.current = false;
        onHasContentChange(false);
      },
      capture: async () => {
        if (!hasContent.current) return undefined;
        return canvasRef.current?.toDataURL('image/png');
      },
    }));

    return (
      // Raw DOM canvas — resolved through react-native-web's `.web.tsx` platform split.
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        style={{
          width: '100%',
          height: 124,
          display: 'block',
          touchAction: 'none',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          opacity: disabled ? 0.6 : 1,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    );
  },
);

export default SignatureCanvas;
